import { useState, useMemo, Dispatch, SetStateAction } from 'react';

import { Posts } from '@/models/posts';

export interface MediaStateType {
  activeIndex: number;
  setActiveIndex: Dispatch<SetStateAction<number>>;
  videoPlaybackStart?: number;
  setVideoPlaybackStart: Dispatch<SetStateAction<number | undefined>>;
  videoLastClick: string | number;
  setVideoLastClick: Dispatch<SetStateAction<string | number>>;
  videoPlayedSeconds: number;
  setVideoPlayedSeconds: Dispatch<SetStateAction<number>>;
  showIdentificationMarkers: boolean;
  setIdentificationMarkers: Dispatch<SetStateAction<boolean>>;
  selectedMinorItems: Posts.MinorItem[];
  setSelectedMinorItems: Dispatch<SetStateAction<Posts.MinorItem[]>>;
  selectedOcrAdvItems: Array<Posts.OcrAdvImageItem>;
  setSelectedOcrAdvItems: Dispatch<SetStateAction<Posts.OcrAdvImageItem[]>>;
  selectedLogoItems: Array<Posts.LogoItem>;
  setSelectedLogoItems: Dispatch<SetStateAction<Posts.LogoItem[]>>;
  markerItems: (Posts.MinorItem | Posts.OcrAdvImageItem | Posts.LogoItem)[];
  darkenImageStyle: string;
  resetMarkers: () => void;
  resetMediaState: () => void;
}

export const INITIAL_MEDIA_VALUES: MediaStateType = {
  activeIndex: 0,
  setActiveIndex: () => {
    throw new Error('setActiveIndex not initialized');
  },
  videoPlaybackStart: undefined,
  setVideoPlaybackStart: () => {
    throw new Error('setVideoPlaybackStart not initialized');
  },
  videoLastClick: new Date().toTimeString(),
  setVideoLastClick: () => {
    throw new Error('setVideoLastClick not initialized');
  },
  videoPlayedSeconds: 0,
  setVideoPlayedSeconds: () => {
    throw new Error('setVideoPlayedSeconds not initialized');
  },
  showIdentificationMarkers: true,
  setIdentificationMarkers: () => {
    throw new Error('setIdentificationMarkers not initialized');
  },
  selectedMinorItems: [],
  setSelectedMinorItems: () => {
    throw new Error('setSelectedMinorItems not initialized');
  },
  selectedOcrAdvItems: [],
  setSelectedOcrAdvItems: () => {
    throw new Error('setSelectedOcrAdvItems not initialized');
  },
  selectedLogoItems: [],
  setSelectedLogoItems: () => {
    throw new Error('setSelectedLogoItems not initialized');
  },
  markerItems: [],
  darkenImageStyle: '',
  resetMarkers: () => {},
  resetMediaState: () => {}
};

const useMedia = (): MediaStateType => {
  const [activeIndex, setActiveIndex] = useState<number>(
    INITIAL_MEDIA_VALUES.activeIndex
  );
  const [videoPlaybackStart, setVideoPlaybackStart] = useState<
    number | undefined
  >(INITIAL_MEDIA_VALUES.videoPlaybackStart);
  const [videoLastClick, setVideoLastClick] = useState<string | number>(
    INITIAL_MEDIA_VALUES.videoLastClick
  );
  const [videoPlayedSeconds, setVideoPlayedSeconds] = useState<number>(
    INITIAL_MEDIA_VALUES.videoPlayedSeconds
  );
  const [showIdentificationMarkers, setIdentificationMarkers] =
    useState<boolean>(INITIAL_MEDIA_VALUES.showIdentificationMarkers);
  const [selectedMinorItems, setSelectedMinorItems] = useState<
    Posts.MinorItem[]
  >([]);
  const [selectedOcrAdvItems, setSelectedOcrAdvItems] = useState<
    Posts.OcrAdvImageItem[]
  >([]);
  const [selectedLogoItems, setSelectedLogoItems] = useState<Posts.LogoItem[]>(
    []
  );

  const markerItems = useMemo(
    () => [...selectedMinorItems, ...selectedOcrAdvItems, ...selectedLogoItems],
    [selectedMinorItems, selectedOcrAdvItems, selectedLogoItems]
  );

  const darkenImageStyle = useMemo(
    () =>
      showIdentificationMarkers &&
      (selectedMinorItems.length > 0 ||
        selectedOcrAdvItems.length > 0 ||
        selectedLogoItems.length > 0)
        ? 'brightness(75%) contrast(75%)'
        : '',
    [
      showIdentificationMarkers,
      selectedMinorItems,
      selectedOcrAdvItems,
      selectedLogoItems
    ]
  );

  const resetMarkers = () => {
    setSelectedMinorItems([]);
    setSelectedOcrAdvItems([]);
    setSelectedLogoItems([]);
  };

  const resetMediaState = () => {
    setActiveIndex(INITIAL_MEDIA_VALUES.activeIndex);
    setVideoPlaybackStart(INITIAL_MEDIA_VALUES.videoPlaybackStart);
    setVideoLastClick(INITIAL_MEDIA_VALUES.videoLastClick);
    setVideoPlayedSeconds(INITIAL_MEDIA_VALUES.videoPlayedSeconds);
    setIdentificationMarkers(INITIAL_MEDIA_VALUES.showIdentificationMarkers);
    setSelectedMinorItems([]);
    setSelectedOcrAdvItems([]);
    setSelectedLogoItems([]);
  };

  return {
    activeIndex,
    setActiveIndex,
    videoPlaybackStart,
    setVideoPlaybackStart,
    videoLastClick,
    setVideoLastClick,
    videoPlayedSeconds,
    setVideoPlayedSeconds,
    showIdentificationMarkers,
    setIdentificationMarkers,
    selectedMinorItems,
    setSelectedMinorItems,
    selectedOcrAdvItems,
    setSelectedOcrAdvItems,
    selectedLogoItems,
    setSelectedLogoItems,
    markerItems,
    darkenImageStyle,
    resetMarkers,
    resetMediaState
  };
};

export default useMedia;
